import type { FlatEvent, SessionDetail } from "@backstory/protocol";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { CopyButton } from "../../components/ui";
import { captureSummary, captureVerdict, LEVEL_EFFECT, LEVEL_LABEL, REASON_LABEL } from "../../lib/capture";
import { formatLocation } from "../../lib/country";
import { formatBytes, formatDateTime, formatDuration, privacyLabel } from "../../lib/format";
import { parseUA } from "../../lib/ua";

function value(v: unknown): string {
  if (v === undefined || v === null || v === "") return "Not captured";
  return String(v);
}

function Row({ label, children, copy }: { label: string; children: ReactNode; copy?: string }) {
  return (
    <div className="session-info-row">
      <dt>{label}</dt>
      <dd>{children}{copy ? <CopyButton value={copy} /> : null}</dd>
    </div>
  );
}

/**
 * What the recorder cost the page, and therefore how much of this replay can
 * be trusted to be complete. Everything is derived from the recorder's own
 * control events; a session recorded before health reporting says so rather
 * than implying a clean bill of health.
 */
function CaptureHealth({ session, events }: { session: SessionDetail; events: FlatEvent[] }) {
  const health = useMemo(() => captureSummary(session, events), [session, events]);
  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

  return (
    <section data-testid="capture-health">
      <h3>Capture health</h3>
      <p className="session-info-verdict">{captureVerdict(health, session.durationMs)}</p>
      <dl>
        <Row label="Worst level">{LEVEL_LABEL[health.worst]}{health.worst !== "HEALTHY" ? <small>{LEVEL_EFFECT[health.worst]}</small> : null}</Row>
        {health.reported ? (
          <Row label="Time at each level">
            {Object.entries(health.msAtLevel)
              .filter(([, ms]) => (ms ?? 0) > 0)
              .map(([level, ms]) => `${LEVEL_LABEL[level as keyof typeof LEVEL_LABEL]} ${formatDuration(ms ?? 0)}`)
              .join(" · ")}
          </Row>
        ) : null}
        {health.reasons.length ? (
          <Row label="Why detail dropped">
            <ul className="session-info-list">
              {health.reasons.map((r) => (
                <li key={`${r.reason}-${r.level}`}>
                  {LEVEL_LABEL[r.level]} for {formatDuration(r.ms)} — {REASON_LABEL[r.reason]}
                  {r.count > 1 ? ` (${r.count} times)` : ""}
                </li>
              ))}
            </ul>
          </Row>
        ) : null}
        <Row label="Dropped replay">{health.droppedBytes ? `${formatBytes(health.droppedBytes)} never uploaded` : value(health.reported ? "Nothing dropped" : undefined)}</Row>
        <Row label="Gaps">{session.hasGaps ? session.gaps.map((g) => `${formatDuration(g.ms)} (${g.reason})`).join(" · ") : "None"}</Row>
        <Row label="Peak queue drain">{health.peakDrainSec !== undefined ? `${health.peakDrainSec.toFixed(1)} s of backlog` : value(undefined)}</Row>
        <Row label="Peak memory use">{health.peakMemPct !== undefined ? `${pct(health.peakMemPct)} of the recording budget` : value(undefined)}</Row>
        <Row label="Slowest upload">{health.slowestGoodputBps !== undefined ? `${formatBytes(health.slowestGoodputBps)}/s` : value(undefined)}</Row>
        <Row label="Recorder jank">{health.peakSelfJank !== undefined ? `${pct(health.peakSelfJank)} of long-task time` : value(undefined)}</Row>
        <Row label="Local buffering">{health.peakSpillBytes ? `${formatBytes(health.peakSpillBytes)} held on device` : value(health.reported ? "Never needed" : undefined)}</Row>
        <Row label="Connectivity">{health.reported ? (health.offline ? "Went offline during capture" : "Online throughout") : value(undefined)}</Row>
        <Row label="Clock offset">{health.clockOffsetMs !== undefined ? `${health.clockOffsetMs} ms from server time` : value(undefined)}</Row>
      </dl>
    </section>
  );
}

export function SessionInformationPanel({ session, events = [] }: { session: SessionDetail; events?: FlatEvent[] }) {
  const ua = parseUA(session.userAgent);
  const location = formatLocation(session);
  const connection = [
    session.connectionEffectiveType,
    session.connectionDownlinkMbps ? `${session.connectionDownlinkMbps} Mbps` : "",
    session.connectionSaveData ? "data saver" : "",
  ].filter(Boolean).join(" · ");

  return (
    <div className="session-info scroll-cue">
      <section>
        <h3>User and review</h3>
        <dl>
          <Row label="User">{value(session.endUserName ?? session.endUserId)}</Row>
          <Row label="User ID" copy={session.endUserId}>{value(session.endUserId)}</Row>
          <Row label="Review state">{session.reviewedAt ? `Reviewed ${formatDateTime(session.reviewedAt)}` : session.lastViewedAt ? "Viewed, not reviewed" : "Not viewed"}</Row>
          <Row label="Reviewed by">{value(session.reviewedBy)}</Row>
          <Row label="Last viewed">{session.lastViewedAt ? formatDateTime(session.lastViewedAt) : "Not viewed"}</Row>
        </dl>
      </section>

      <section>
        <h3>Device and network</h3>
        <dl>
          <Row label="Browser">{value(session.browser ?? ua.browser)}</Row>
          <Row label="Operating system">{value(session.os ?? ua.os)}</Row>
          <Row label="Device">{value(session.device)}</Row>
          <Row label="IP address" copy={session.ipAddress}>{value(session.ipAddress)}{session.ipAddress ? <small>Server observed</small> : null}</Row>
          <Row label="Location">{location || "Not captured"}{location ? <small>Approximate</small> : null}</Row>
          <Row label="Viewport">{session.viewport.w}×{session.viewport.h} · {session.viewport.dpr || 1}× DPR</Row>
          <Row label="Memory">{session.deviceMemoryGB ? `${session.deviceMemoryGB} GB` : "Not captured"}</Row>
          <Row label="Connection">{value(connection)}</Row>
          <Row label="Language">{value(session.language)}</Row>
          <Row label="Timezone">{value(session.timezone)}</Row>
          <Row label="User agent" copy={session.userAgent}>{value(session.userAgent)}</Row>
        </dl>
      </section>

      <section>
        <h3>Page and application</h3>
        <dl>
          <Row label="Page title">{value(session.title)}</Row>
          <Row label="URL" copy={session.url}>{value(session.url)}</Row>
          <Row label="Referrer" copy={session.referrer}>{value(session.referrer)}</Row>
          <Row label="Environment">{value(session.environment)}</Row>
          <Row label="Release">{value(session.release)}</Row>
          <Row label="SDK version">{value(session.sdkVersion)}</Row>
        </dl>
      </section>

      <section>
        <h3>Recording</h3>
        <dl>
          <Row label="Session ID" copy={session.id}>{session.id}</Row>
          <Row label="Visit ID" copy={session.visitId}>{session.visitId}</Row>
          <Row label="Surface / segment">{session.surfaceId + 1} / {session.segmentIndex}</Row>
          <Row label="Started">{formatDateTime(session.startedAt)}</Row>
          <Row label="Duration">{formatDuration(session.durationMs)}</Row>
          <Row label="Status">{value(session.status)}</Row>
          <Row label="Capture mode">{session.recordingMode} · {privacyLabel(session.privacyMode)}</Row>
          <Row label="Volume">{session.eventCount.toLocaleString()} events · {session.chunkCount} chunks · {formatBytes(session.bytes)}</Row>
          <Row label="Signals">{session.clickCount} clicks · {session.errorCount} errors · {session.hasGaps ? "has gaps" : "no gaps"}</Row>
        </dl>
      </section>

      <CaptureHealth session={session} events={events} />
      {session.ipAddress || location ? <p className="session-info-privacy">The network address and its approximate location are personal data. They follow the session retention policy and are omitted from public share links.</p> : null}
    </div>
  );
}
